/**
 * OpenUI NotesBrowser — flat live notes list (click opens note modal).
 */

'use client'

import { useCallback, useContext, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { ChatbotUIContext } from '@/context/context'
import {
  openUIPanelClass,
  openUIRowClass,
  openUISubtitleClass,
  openUITitleClass
} from '@/lib/openui/chrome'
import { dispatchSidekickOpenSpaceItem } from '@/lib/sidekick/space-item-context'
import { cn } from '@/lib/utils'

export type OpenUINotesBrowserProps = {
  title?: string
  spaceId?: string | null
  limit?: number
  className?: string
}

type NoteRow = {
  id: string
  title: string
  updated_at: string
}

export function OpenUINotesBrowser({
  title = 'Notes',
  spaceId: spaceIdProp,
  limit = 20,
  className
}: OpenUINotesBrowserProps) {
  const { selectedSpace } = useContext(ChatbotUIContext)
  const spaceId = (spaceIdProp || selectedSpace?.id || '').trim()
  const [rows, setRows] = useState<NoteRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!spaceId) {
      setRows([])
      setError('Select a Space to load notes.')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(
        `/api/spaces/${encodeURIComponent(spaceId)}/notes`,
        { credentials: 'include', headers: { Accept: 'application/json' } }
      )
      if (!res.ok) throw new Error(`Failed to load notes (${res.status})`)
      const data = await res.json()
      const list = Array.isArray(data) ? data : []
      setRows(
        list.slice(0, Math.min(Math.max(limit, 1), 50)).map((item: Record<string, unknown>) => ({
          id: typeof item.id === 'string' ? item.id : String(item.id || ''),
          title:
            typeof item.title === 'string'
              ? item.title
              : typeof item.name === 'string'
                ? item.name
                : 'Untitled note',
          updated_at:
            typeof item.updated_at === 'string'
              ? item.updated_at
              : typeof item.updatedAt === 'string'
                ? item.updatedAt
                : ''
        }))
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load notes')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [limit, spaceId])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const openNote = (note: NoteRow) => {
    if (!spaceId) {
      toast.error('No Space selected')
      return
    }
    dispatchSidekickOpenSpaceItem({
      spaceId,
      type: 'note',
      id: note.id,
      title: note.title
    })
  }

  if (!spaceId) {
    return (
      <div className={cn('px-1 py-3', openUISubtitleClass, className)}>
        Select a Space to browse live notes.
      </div>
    )
  }

  return (
    <section
      className={cn(openUIPanelClass, className)}
      data-openui-notes-browser="true"
    >
      <div className="flex items-center justify-between gap-2 px-1">
        <h4 className={openUITitleClass}>{title}</h4>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs"
          onClick={() => void refresh()}
          disabled={loading}
        >
          Refresh
        </Button>
      </div>
      {error ? (
        <p className="px-1 text-[12px] text-destructive">{error}</p>
      ) : null}
      {loading && rows.length === 0 ? (
        <div className="h-16 animate-pulse rounded-lg bg-foreground/[0.04]" aria-hidden="true" />
      ) : rows.length === 0 ? (
        <p className={cn('px-1 py-4 text-center', openUISubtitleClass)}>
          No notes in this Space yet.
        </p>
      ) : (
        <ul>
          {rows.map(note => (
            <li key={note.id}>
              <button
                type="button"
                className={cn(openUIRowClass, 'text-left')}
                onClick={() => openNote(note)}
              >
                <span className="truncate text-[13px] font-medium text-foreground">
                  {note.title}
                </span>
                {note.updated_at ? (
                  <span className="shrink-0 text-[11px] text-muted-foreground">
                    {note.updated_at.slice(0, 10)}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
